// How fast the dashed connectors between stages move (TTTL-251): the
// dashes travel one period per cycle, and the period follows the
// pipeline's throughput. Pure, so the mapping is testable without a DOM.

/** Seconds per dash cycle at the slowest crawl and the fastest rush. */
export const SLOWEST_PERIOD_S = 4;
export const FASTEST_PERIOD_S = 0.25;
/** Throughput of one event per second yields this period: a pipeline
 *  doing a few events a second rushes, one doing a few a minute crawls. */
const REFERENCE_PERIOD_S = 0.5;

/** The dash period for a throughput in events per second: null when nothing
 *  flows (the dashes stand still), otherwise inversely proportional to the
 *  rate and clamped between a crawl and a rush. Unknown or malformed rates
 *  stand still too. */
export function dashPeriodSeconds(
  eventsPerSecond: number | null | undefined,
): number | null {
  if (!isMovingRate(eventsPerSecond)) return null;
  const period = REFERENCE_PERIOD_S / eventsPerSecond;
  return Math.min(SLOWEST_PERIOD_S, Math.max(FASTEST_PERIOD_S, period));
}

/** How fast the dashes travel, in pixels per second, for a period: one
 *  dash cycle per period, or nothing when the connector stands still. */
export function dashSpeed(periodS: number | null, cyclePx: number): number {
  if (periodS === null) return 0;
  return cyclePx / periodS;
}

/** Where the dashes are and how fast they move: `phase` is the offset
 *  along the cycle in pixels, `speed` in pixels per second. */
export interface DashMotion {
  phase: number;
  speed: number;
}

/** How long a change of pace takes to mostly settle: the speed closes
 *  this fraction of the gap to its target every RAMP_S. */
const RAMP_S = 0.4;
/** Below this gap the speed is as good as on target and snaps to it, so a
 *  connector at rest stops needing frames. */
const SPEED_SNAP = 0.05;

/** One frame of dash motion: the speed eases toward `targetSpeed` (so a
 *  change of pace slows down or speeds up rather than jumping), and the
 *  phase advances by the eased speed, wrapping at the cycle. The phase
 *  is continuous by construction whatever the target does, which is what
 *  keeps the dashes from snapping back when a poll re-derives the pace. */
export function stepDashMotion(
  motion: DashMotion,
  targetSpeed: number,
  dtS: number,
  cyclePx: number,
): DashMotion {
  const gap = targetSpeed - motion.speed;
  const speed =
    Math.abs(gap) < SPEED_SNAP
      ? targetSpeed
      : motion.speed + gap * (1 - Math.exp(-dtS / RAMP_S));
  const phase = (motion.phase + speed * dtS) % cyclePx;
  return { phase, speed };
}

/** A rate that moves the dashes: a finite number above zero. */
function isMovingRate(rate: number | null | undefined): rate is number {
  if (typeof rate !== "number") return false;
  if (!Number.isFinite(rate)) return false;
  return rate > 0;
}

/** One reading of a pipeline's processed-events counter, taken at `at`
 *  (epoch milliseconds). */
export interface CountSample {
  at: number;
  events: number;
}

/** How far back the pace looks: long enough that a record every half
 *  minute still reads as a crawl rather than a stop. */
export const PACE_WINDOW_MS = 60_000;

/** The samples still inside the window ending at `now`, oldest first. */
export function withinWindow(
  samples: CountSample[],
  now: number,
  windowMs = PACE_WINDOW_MS,
): CountSample[] {
  return samples.filter((s) => s.at >= now - windowMs);
}

/** Events per second across the samples in the window: the counter's rise
 *  from the oldest to the newest reading over the time between them. A
 *  counter that fell is a restart, so only the readings since the latest
 *  fall count: the reset itself reads as zero and the next reading
 *  recovers, rather than the window staying at zero until the old
 *  readings age out. Fewer than two readings, or readings less than a
 *  second apart, say nothing yet and read as zero. The engine's own
 *  throughput figure is not used: it is zero whenever the buffer is
 *  drained and a lifetime average otherwise. */
export function eventsPerSecond(samples: CountSample[]): number {
  const run = sinceLatestFall(samples);
  if (run.length < 2) return 0;
  const first = run[0];
  const last = run[run.length - 1];
  const spanS = (last.at - first.at) / 1000;
  if (spanS < 1) return 0;
  return Math.max(0, (last.events - first.events) / spanS);
}

/** The samples from the latest counter decrease on, or all of them when
 *  the counter never fell. */
function sinceLatestFall(samples: CountSample[]): CountSample[] {
  for (let i = samples.length - 1; i > 0; i--) {
    if (samples[i].events < samples[i - 1].events) return samples.slice(i);
  }
  return samples;
}

/** What the flow card knows of the running pipeline: the pace on each
 *  hop and the state of the one queue the engine measures, the event
 *  stream between the source reader and the workers.
 *
 *  `intoTransform` is the source read rate, from the lineage snapshots'
 *  count of records seen before the transform (a minute's resolution);
 *  `intoSinks` is the rate of records the transform passed on, from the
 *  processed counter less the records it dropped (five seconds'
 *  resolution). Null on either is a pipeline that is not running.
 *
 *  `queued` is the stream's lag, the records read from the source that no
 *  worker has picked up yet; `blocked` is the stream near capacity, with
 *  the source held back. */
export interface FlowState {
  intoTransform: number | null;
  intoSinks: number | null;
  queued: number;
  blocked: boolean;
}

/** The stage a connector leaves. */
export type Hop = "source" | "transform";

/** The pace on a hop, in events per second. */
export function hopPace(state: FlowState, hop: Hop): number | null {
  return hop === "source" ? state.intoTransform : state.intoSinks;
}

/** How a connector reads: queued is a backlog (drawn red, as in the
 *  Metrics card's stream-buffer bar), flowing is records on the move
 *  (green dashes travelling), idle is nothing moving (the same green,
 *  dashes still). Only the connector leaving the source can carry a
 *  backlog, since that is the only queue the engine measures. */
export type ConnectorTone = "idle" | "flowing" | "queued";

export function connectorTone(state: FlowState, hop: Hop): ConnectorTone {
  if (hop === "source" && hasBacklog(state)) return "queued";
  if (isMovingRate(hopPace(state, hop))) return "flowing";
  return "idle";
}

/** Records waiting in the source's stream, or the source held back. */
function hasBacklog(state: FlowState): boolean {
  return state.queued > 0 || state.blocked;
}

/** Events per second read off one line of the records series: the rise
 *  between its last two known moments over the time between them. The
 *  line is cumulative within a deployment, so a fall is a redeploy and
 *  reads as zero. Fewer than two known moments, as in a deployment's
 *  first minute, say nothing yet: null, for the caller to fall back on
 *  another signal rather than draw a still line. */
export function lineRate(
  times: string[],
  values: Array<number | null>,
): number | null {
  const known: Array<{ at: number; value: number }> = [];
  times.forEach((t, i) => {
    const v = values[i];
    if (v !== null && v !== undefined)
      known.push({ at: Date.parse(t), value: v });
  });
  if (known.length < 2) return null;
  const last = known[known.length - 1];
  const prev = known[known.length - 2];
  const spanS = (last.at - prev.at) / 1000;
  if (!(spanS > 0)) return 0;
  return Math.max(0, (last.value - prev.value) / spanS);
}

/** The lines the sink-side pace needs from the records series. */
export interface SeriesRates {
  /** Records read from the source, per second; null while unknown. */
  watched: number | null;
  /** Records the transform dropped, per second; null while unknown. */
  dropped: number | null;
}

export function seriesRates(doc: {
  times: string[];
  series: Array<{ name: string; values: Array<number | null> }>;
}): SeriesRates {
  const line = (name: string) =>
    doc.series.find((s) => s.name === name)?.values ?? [];
  return {
    watched: lineRate(doc.times, line("watched")),
    dropped: lineRate(doc.times, line("dropped")),
  };
}

/** The stage a connector enters, for the tooltip's wording. */
export type HopTarget = "transform" | "sink";

/** The connector's tooltip: the speed on its hop in records per second,
 *  formatted like the Metrics card's rates, and the backlog when the
 *  source's stream holds one. A pipeline that is not running says so.
 *
 *  Between two transforms the connector moves at the rate out of the
 *  last transform and the tooltip gives the range the true rate lies in
 *  (see describeBetweenTransforms). */
export function describeHop(
  state: FlowState,
  hop: Hop,
  formatRate: (rate: number) => string,
  to: HopTarget,
): string {
  const pace = hopPace(state, hop);
  if (pace === null) return "Pipeline not running";
  if (hop === "transform" && to === "transform") {
    return describeBetweenTransforms(pace, state.intoTransform, formatRate);
  }
  const speed = `${formatRate(pace)} events/sec into the ${to}`;
  if (hop !== "source") return speed;
  return `${describeBacklog(state)}${speed}`;
}

/** Between two transforms the engine measures nothing directly, but the
 *  rate is bounded by real measurements: at least what leaves the chain,
 *  at most what entered it. The tooltip gives that range, or the one
 *  figure when the two agree (or the slower series lags behind). No
 *  stage is named: every connector between transforms shows the same
 *  range, since the bounds cover the whole chain. */
function describeBetweenTransforms(
  pace: number,
  intoTransform: number | null,
  formatRate: (rate: number) => string,
): string {
  if (intoTransform !== null && intoTransform > pace) {
    return `between ${formatRate(pace)} and ${formatRate(intoTransform)} events/sec`;
  }
  return `${formatRate(pace)} events/sec`;
}

/** The backlog lead-in for the source's connector; empty when there is
 *  none. */
function describeBacklog(state: FlowState): string {
  if (state.blocked) {
    return `Source held back, ${state.queued} events queued · `;
  }
  if (state.queued > 0) return `${state.queued} events queued · `;
  return "";
}
